import React, { useState } from 'react';
import { Button } from '@/features/ui/components/Button';
import { Combobox } from '@/features/ui/components/Combobox';
import { Plus, Trash2, Loader2, MapPin } from 'lucide-react';
import { PathHop } from '@/features/application/types/forms';
import { usePathBuilder } from '../hooks/use-path-builder.hook';
import { LocatorBuilder } from './LocatorBuilder';

interface PathBuilderProps {
  path: PathHop[];
  onPathChange: (newPath: PathHop[]) => void;
  fieldApiName?: string;
}

export const PathBuilder: React.FC<PathBuilderProps> = ({
  path,
  onPathChange,
  fieldApiName: _fieldApiName,
}) => {
  const [editingLocatorIndex, setEditingLocatorIndex] = useState<number | null>(null);

  const {
    primaryObject,
    openComboboxIndex,
    handleHopChange,
    handleAddStep,
    handleRemoveStep,
    handleLocatorChange,
    getRelationshipOptions,
    handleComboboxOpenChange,
    canAddMoreSteps,
    isResolvingNextStep,
    isAddingStep,
    setIsAddingStep,
  } = usePathBuilder({
    path,
    onPathChange,
  });

  if (!primaryObject) {
    return <p className="text-sm text-gray-500">Please select a primary object first.</p>;
  }

  return (
    <div className="space-y-0">
      {/* Primary object - styled as the foundational path hop */}
      <div className="flex items-center py-2 justify-center">
        <div className="flex items-center space-x-3 border rounded-lg p-3 shadow-sm bg-green-50 border-green-200">
          <span className="font-semibold text-green-800">{primaryObject}</span>
          <span className="text-xs text-green-600 font-mono bg-green-100 px-2 py-1 rounded">
            anchor
          </span>
        </div>
      </div>

      {/* Path hops - skip anchor, render relationship hops */}
      {path
        .slice(1) // Skip anchor hop
        .map((hop, index) => {
          const actualIndex = index + 1; // Adjust for skipped anchor
          const parentHop = path[index]; // Previous hop (index maps correctly since we sliced)

          if (!parentHop) return null;

          const { allPossibleNextObjects, isLoading } = getRelationshipOptions(parentHop.object);
          const isChildRelationship = hop.kind === 'child';
          const needsLocator = isChildRelationship;

          return (
            <div key={`${parentHop.object}-${actualIndex}`}>
              {/* Vertical connector line */}
              <div className="flex justify-center">
                <div className="w-px h-4 bg-gray-300"></div>
              </div>

              {/* Path hop line */}
              <div className="flex items-center justify-center py-2">
                <div className="flex items-center space-x-3 border rounded-lg p-3 shadow-sm bg-blue-50 border-blue-200">
                  <div className="flex items-center space-x-2">
                    <Combobox
                      value={hop.rel || ''}
                      onChange={(value) => handleHopChange(actualIndex, value)}
                      options={allPossibleNextObjects}
                      placeholder={isLoading ? 'Loading...' : `Select relationship...`}
                      searchPlaceholder="Search relationships..."
                      noResultsMessage="No relationships found."
                      triggerClassName="min-w-64"
                      contentClassName="min-w-64"
                      open={openComboboxIndex === actualIndex}
                      onOpenChange={(open) => handleComboboxOpenChange(actualIndex, open)}
                    />
                    <Button
                      variant="outline"
                      onClick={() => handleRemoveStep(actualIndex)}
                      title="Remove this step and all subsequent steps"
                      className="p-2 hover:bg-red-50 flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Locator section for child relationships */}
              {needsLocator && (
                <>
                  {/* Vertical connector line to locator */}
                  <div className="flex justify-center">
                    <div className="w-px h-3 bg-blue-300"></div>
                  </div>

                  {/* Locator line */}
                  <div className="flex items-start justify-center py-2">
                    {editingLocatorIndex === actualIndex || (!hop.locator && needsLocator) ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3 min-w-80">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-blue-800 flex items-center">
                            <MapPin className="h-4 w-4 mr-1" />
                            Locator for {hop.object}
                          </div>
                          <Button
                            variant="outline"
                            onClick={() => {
                              handleLocatorChange(actualIndex, null);
                              setEditingLocatorIndex(null);
                            }}
                            title="Clear locator"
                            className="p-2 hover:bg-red-50 flex-shrink-0"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                        <LocatorBuilder
                          objectType={hop.object}
                          initialMode={hop.locator?.strategy || 'fieldValue'}
                          initialFieldApiName={
                            hop.locator?.strategy === 'fieldValue'
                              ? Object.keys(hop.locator?.params || {})[0]
                              : undefined
                          }
                          initialValue={
                            hop.locator?.strategy === 'fieldValue'
                              ? Object.values(hop.locator?.params || {})[0]
                              : undefined
                          }
                          initialSortField={
                            hop.locator?.strategy === 'positional'
                              ? hop.locator?.params?.orderBy
                              : undefined
                          }
                          initialSortDirection={
                            hop.locator?.strategy === 'positional'
                              ? hop.locator?.params?.direction
                              : undefined
                          }
                          initialIndex={
                            hop.locator?.strategy === 'positional'
                              ? hop.locator?.params?.index
                              : undefined
                          }
                          onSave={(locator) => {
                            handleLocatorChange(actualIndex, locator);
                            setEditingLocatorIndex(null);
                          }}
                          onCancel={() => setEditingLocatorIndex(null)}
                        />
                      </div>
                    ) : hop.locator ? (
                      <div
                        className="flex items-center space-x-3 bg-blue-100 border border-blue-300 rounded-lg p-2 shadow-sm cursor-pointer hover:bg-blue-200"
                        onClick={() => setEditingLocatorIndex(actualIndex)}
                      >
                        <MapPin className="h-4 w-4 text-blue-600" />
                        <span className="text-sm text-blue-800 font-mono">
                          {hop.locator.strategy === 'fieldValue'
                            ? `${Object.keys(hop.locator.params)[0]}=${Object.values(hop.locator.params)[0]}`
                            : `pos[${hop.locator.params.index || 0}]`}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          );
        })}

      {/* New hop when adding step */}
      {isAddingStep && (
        <div key={`adding-${path.length}`}>
          {/* Vertical connector line */}
          <div className="flex justify-center">
            <div className="w-px h-4 bg-gray-300"></div>
          </div>

          {/* Path hop line */}
          <div className="flex items-center justify-center py-2">
            <div className="flex items-center space-x-3 border rounded-lg p-3 shadow-sm bg-white border-gray-200">
              <div className="flex items-center space-x-2">
                {(() => {
                  const parentObject = path[path.length - 1]?.object || primaryObject;
                  const { allPossibleNextObjects, isLoading } =
                    getRelationshipOptions(parentObject);

                  return (
                    <Combobox
                      value=""
                      onChange={(value) => handleHopChange(path.length, value)}
                      options={allPossibleNextObjects}
                      placeholder={isLoading ? 'Loading...' : `Select relationship...`}
                      searchPlaceholder="Search relationships..."
                      noResultsMessage="No relationships found."
                      triggerClassName="min-w-64"
                      contentClassName="min-w-64"
                      open={openComboboxIndex === path.length}
                      onOpenChange={(open) => handleComboboxOpenChange(path.length, open)}
                    />
                  );
                })()}
                <Button
                  variant="outline"
                  onClick={() => setIsAddingStep(false)}
                  title="Cancel adding step"
                  className="p-2 hover:bg-gray-50 flex-shrink-0"
                >
                  <Trash2 className="h-4 w-4 text-gray-500" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Step Button */}
      {(canAddMoreSteps() || isResolvingNextStep()) && !isAddingStep && (
        <>
          <div className="flex justify-center">
            <div className="w-px h-4 bg-gray-300"></div>
          </div>
          <div className="flex justify-center py-2">
            <Button
              variant="outline"
              onClick={handleAddStep}
              disabled={isResolvingNextStep() || !canAddMoreSteps()}
              className="flex items-center space-x-2"
            >
              {isResolvingNextStep() ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              <span>Add Step</span>
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
